import type { ReactNode } from "react";
import { useI18n } from "./i18n";
import { useWorkspace } from "./workspace";
import { StatePanel, Button } from "@anyseal/ui";

/**
 * Wraps pages that require an active workspace (organization + project).
 *
 * Renders loading / error / empty-onboarding states automatically so every
 * page just renders its real content when the workspace is ready.
 */
export function WorkspaceGuard({ children }: { children: ReactNode }) {
  const { locale } = useI18n();
  const { organization, project, loading, error } = useWorkspace();
  const isChinese = locale === "zh-CN";

  if (loading) {
    return (
      <StatePanel
        kind="loading"
        title={isChinese ? "正在准备工作区…" : "Preparing workspace…"}
      />
    );
  }

  if (error) {
    return (
      <StatePanel
        kind="error"
        title={isChinese ? "工作区加载失败" : "Workspace unavailable"}
        description={error.message}
        action={
          <Button variant="secondary" onClick={() => window.location.reload()}>
            {isChinese ? "刷新页面" : "Reload page"}
          </Button>
        }
      />
    );
  }

  if (!organization || !project) {
    return (
      <StatePanel
        kind="empty"
        title={isChinese ? "还没有可用的项目" : "No project yet"}
        description={
          isChinese
            ? "请联系组织管理员创建项目，或者通过顶部左侧的工作区切换器选择一个项目。"
            : "Ask your organization admin to create a project, or use the workspace switcher in the top-left to select one."
        }
      />
    );
  }

  return <>{children}</>;
}
